<template>
  <el-form
      @submit.prevent="validateSubmit('noteForm')"
      ref="noteForm"
      :model="documentation"
      label-position="top"
      novalidate
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('noteForm')"
          />
        </div>
      </div>
    </input-section>

    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('documentation') }}</h1>

      <div class="flex flex-col md:flex-row gap-4 w-full">
        <el-form-item :label="t('name')" class="input-label flex-1" required>
          <el-autocomplete
              v-model="documentation.name"
              :fetch-suggestions="searchNames"
              clearable
              class="w-full"
              @select="onNameSelected"
              @clear="onNameCleared"
              placeholder=""
          >
            <template #default="{ item }">
              <span style="float: left">{{ item.name }}</span>
              <span
                  style="
                    float: right;
                    color: var(--el-text-color-secondary);
                    font-size: 13px;
                  "
              >
                {{ item.phone ? item.phone + ' ' : '' }}({{ item.source }})
              </span>
            </template>
          </el-autocomplete>
        </el-form-item>

        <el-form-item :label="t('phone')" class="input-label flex-1">
          <el-input v-model="documentation.phone" style="direction: ltr" />
        </el-form-item>

        <el-form-item :label="t('contactVia')" class="input-label flex-1">
          <el-select v-model="documentation.contactVia" clearable class="w-full">
            <el-option :label="t('phoneCall')" value="phone_call" />
            <el-option :label="t('message')" value="message" />
            <el-option :label="t('inPerson')" value="in_person" />
          </el-select>
        </el-form-item>
      </div>

      <el-form-item :label="t('subject')" class="input-label">
        <el-input v-model="documentation.subject" />
      </el-form-item>

      <el-form-item :label="t('note')" class="input-label">
        <el-input v-model="documentation.note" type="textarea" :rows="3" />
      </el-form-item>

      <!-- Related Task -->
      <div class="mt-4">
        <InlineTaskEditor
          ref="inlineTaskEditor"
          :task="localTask"
          :users="users"
          :note-id="documentation.id || ''"
          :hide-create-save="!documentation.id"
          @task-created="onTaskCreated"
          @task-deleted="onTaskDeleted"
        />
      </div>
    </input-section>

  </el-form>

  <el-dialog v-model="cashierPinDialogOpen" width="30%">
    <div>
      <div class="my-2">
        <p>{{ t('enterCashierPin') }}</p>
      </div>

      <div class="my-2">
        <el-input
            v-model="cashierPin"
            type="password"
            required
            show-password
            autofocus
            dir="ltr"
            :maxlength="4"
            :minlength="4"
            @keyup.enter="submitWithPin"
            ref="cashierPinInput"
        />
      </div>

      <div class="flex items-center justify-between mb-2">
        <horizontal-button
            :title="$t('actions.saveChanges')"
            isSuccess
            @click="submitWithPin"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script lang="ts">
import {defineComponent, nextTick, PropType} from 'vue'
import {useI18n} from 'vue-i18n'
import Documentation, {documentationI18nMessages} from '@/models/customer/Documentation'
import Customer from '@/models/customer/Customer'
import Task from '@/models/hr/Task'
import User from '@/models/user/User'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import InlineTaskEditor from './InlineTaskEditor.vue'
import {useSettingsStore} from '@/stores/settingsStore'

interface NameOption {
  id: string
  value: string
  name: string
  phone: string
  source: string
  customerID?: string
  vendorID?: string
}

export default defineComponent({
  name: 'NoteForm',

  components: {
    HorizontalButton,
    InputSection,
    AlertBadge,
    InlineTaskEditor,
  },

  props: {
    initDocumentation: {
      type: Object as PropType<Documentation>,
      required: true
    },
    submitAction: {
      type: Function as PropType<(doc: Documentation, pin?: string) => Promise<any>>,
      required: true
    },
    successMsg: {
      type: String,
      default: ''
    }
  },

  setup() {
    const {t} = useI18n({
      useScope: 'global',
      messages: {
        en: {
          ...documentationI18nMessages.en,
          enterCashierPin: 'Enter cashier pin',
          errCashierPinRequired: 'Cashier pin is required',
        },
        ar: {
          ...documentationI18nMessages.ar,
          enterCashierPin: 'ادخل رمز الكاشير الخاص بك',
          errCashierPinRequired: 'يرجى ادخال رمز الكاشير',
        }
      }
    })

    return {t}
  },

  data() {
    return {
      documentation: new Documentation(),
      nameOptions: [] as NameOption[],
      error: {title: '', body: ''},
      cashierPinDialogOpen: false,
      cashierPin: '',
      localTask: null as Task | null,
      users: [] as User[],
    }
  },

  mounted() {
    Object.assign(this.documentation, this.initDocumentation)
    this.localTask = this.initDocumentation.task ?? null
    this.$http.get<User[]>('/user/users').then(res => {
      this.users = res || []
    }).catch(() => {})
  },

  methods: {
    async searchNames(query: string, cb: (results: NameOption[]) => void) {
      if (!query || query.length < 1) {
        cb([])
        return
      }

      try {
        const [customers, vendors] = await Promise.all([
          this.$http.get<Customer[]>(Customer.ENDPOINT + `?q=${encodeURIComponent(query)}`),
          this.$http.get<any[]>('/vendor/vendors' + `?q=${encodeURIComponent(query)}`)
        ])

        const customerOptions: NameOption[] = (customers || []).map((c: any) => ({
          id: c.id,
          value: c.name || '',
          name: c.name || '',
          phone: c.phone || '',
          source: this.$ctx.locale == 'ar' ? 'زبون' : 'Customer',
          customerID: c.id,
        }))

        const vendorOptions: NameOption[] = (vendors || []).map((v: any) => ({
          id: v.id,
          value: v.name || '',
          name: v.name || '',
          phone: v.phone || '',
          source: this.$ctx.locale == 'ar' ? 'مورد' : 'Vendor',
          vendorID: v.id,
        }))

        const results = [...customerOptions, ...vendorOptions]
        this.nameOptions = results
        cb(results)
      } catch (e) {
        console.log(e)
        cb([])
      }
    },

    onNameSelected(item: NameOption) {
      this.documentation.name = item.name
      if (item.phone) {
        this.documentation.phone = item.phone
      }
      if (item.customerID) {
        this.documentation.customerID = item.customerID
        this.documentation.vendorID = null
      } else if (item.vendorID) {
        this.documentation.vendorID = item.vendorID
        this.documentation.customerID = null
      }
      this.documentation.customer = undefined
    },

    onNameCleared() {
      this.documentation.customerID = null
      this.documentation.vendorID = null
      this.documentation.customer = undefined
    },

    async validateSubmit(formName: string) {
      if (!this.documentation.name) {
        this.$alertModal.showDanger({
          title: this.$ctx.locale == 'ar' ? 'الاسم مطلوب' : 'Name is required'
        })
        return
      }

      // For new notes: auto-save a pending task form before saving the note
      if (!this.documentation.id) {
        const editor = this.$refs.inlineTaskEditor as any
        if (editor?.isCreating) {
          await editor.saveTask()
          // If isCreating is still true, task form validation failed — block note save
          if (editor.isCreating) return
        }
      }

      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        this.cashierPinDialogOpen = true
        nextTick(() => {
          setTimeout(() => {
            (this.$refs.cashierPinInput as any)?.focus()
          }, 0)
        })
        return
      }

      this.doSubmit()
    },

    async submitWithPin() {
      if (this.cashierPin === '') {
        this.$alertModal.showDanger({
          title: this.t('errCashierPinRequired')
        })
        return
      }
      this.cashierPinDialogOpen = false
      this.doSubmit(this.cashierPin)
    },

    onTaskCreated(task: Task) {
      this.localTask = task
      // If note doesn't have an ID yet, store the pending task ID for the backend to link on create
      if (!this.documentation.id) {
        this.documentation.pendingTaskID = task.id || ''
      }
    },

    onTaskDeleted() {
      this.localTask = null
      this.documentation.pendingTaskID = ''
    },

    async doSubmit(pin?: string) {
      try {
        const error = await this.submitAction(this.documentation, pin)
        if (error) {
          this.error.title = error.title
          this.error.body = error.body
          document.getElementById('main')?.scrollTo({top: 0})
        } else if (this.successMsg) {
          this.$alertModal.showSuccess({title: this.successMsg})
        }
      } catch (error) {
        this.error.title = error.title
        this.error.body = error.body
        document.getElementById('main')?.scrollTo({top: 0})
      }
      this.cashierPin = ''
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply text-lg py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
